import type {
  Action,
  ValidationResult,
  ValidationError,
} from "../../types/actions";
import type { Project } from "../../types/project";
import type { Subtitle, SubtitleStyle } from "../../types/timeline";
import type { MutableTimeline } from "../../utils/immutable-updates";
import { createInverseAction, registerActionHandler } from "../registry";
import type { ActionHandler, ActionHandlerContext } from "../registry";

/**
 * Subtitles on the timeline.
 *
 * subtitle/restore and subtitle/restoreAll are minted only by the inverses in
 * this file, never by a caller, which is why neither had a case in the legacy
 * validator or the legacy inverse generator. Both get one here: a type either
 * answers all three questions or the registry cannot stand in for the three
 * switches, and an entry pushed with a null inverse wedges undo, because
 * ActionHistory.undo refuses to pop what it cannot invert and every later press
 * then fails against that same entry instead of reaching the edits below it.
 *
 * subtitle/replace and subtitle/setAll are the other two members of the
 * SubtitleAction union and are not here: they are already handled in
 * bulk-set.ts, and never had a case in the executor's switch at all.
 */

const ok = (): ValidationResult => ({ valid: true, errors: [] });

const err = (message: string, path?: string): ValidationResult => ({
  valid: false,
  errors: [{ code: "INVALID_PARAMS", message, path }],
});

const collect = (errors: ValidationError[]): ValidationResult => ({
  valid: errors.length === 0,
  errors,
});

/**
 * The executor guarded every read with `|| []` and the inverse generator did
 * not. Both are kept as they were: a project that somehow reaches apply without
 * the array still edits, while undo still fails loudly rather than quietly
 * restoring an empty list it invented.
 */
function currentSubtitles(project: Project): Subtitle[] {
  return project.timeline.subtitles || [];
}

function setSubtitles(project: Project, next: Subtitle[]): void {
  (project.timeline as MutableTimeline).subtitles = next;
}

function parseSrtTime(timeString: string): number {
  const [time, ms] = timeString.split(",");
  const [hours, minutes, seconds] = time.split(":").map(Number);
  return hours * 3600 + minutes * 60 + seconds + Number(ms) / 1000;
}

const subtitleAdd: ActionHandler = {
  type: "subtitle/add",

  validate(action: Action): ValidationResult {
    const params = action.params as {
      text?: unknown;
      startTime?: unknown;
      endTime?: unknown;
    };
    const errors: ValidationError[] = [];

    if (!params.text || typeof params.text !== "string") {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Subtitle text is required and must be a string",
        path: "params.text",
      });
    }

    if (typeof params.startTime !== "number" || params.startTime < 0) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Start time must be a non-negative number",
        path: "params.startTime",
      });
    }

    if (typeof params.endTime !== "number" || params.endTime < 0) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "End time must be a non-negative number",
        path: "params.endTime",
      });
    }

    if (
      typeof params.startTime === "number" &&
      typeof params.endTime === "number" &&
      params.endTime <= params.startTime
    ) {
      errors.push({
        code: "INVALID_TIME_RANGE",
        message: "End time must be greater than start time",
        path: "params",
      });
    }

    return collect(errors);
  },

  apply(action: Action, project: Project, ctx: ActionHandlerContext): void {
    const params = action.params as {
      subtitleId?: string;
      text: string;
      startTime: number;
      endTime: number;
    };
    const newSubtitle: Subtitle = {
      id: params.subtitleId ?? `subtitle-${crypto.randomUUID()}`,
      text: params.text,
      startTime: params.startTime,
      endTime: params.endTime,
    };
    // Redo replays this same action object; without this it would mint a
    // fresh id and the stored inverse would target the one from the first run.
    (action.params as Record<string, unknown>).subtitleId = newSubtitle.id;
    setSubtitles(project, [...currentSubtitles(project), newSubtitle]);
    ctx.lastAddedIds.set("subtitle", newSubtitle.id);
  },

  invert(action: Action): Action | null {
    return createInverseAction(action, "subtitle/remove", {
      subtitleId: "__LAST_ADDED__",
    });
  },
};

const subtitleRemove: ActionHandler = {
  type: "subtitle/remove",

  validate(action: Action): ValidationResult {
    const subtitleId = (action.params as { subtitleId?: unknown }).subtitleId;
    if (!subtitleId || typeof subtitleId !== "string") {
      return err(
        "Subtitle ID is required and must be a string",
        "params.subtitleId",
      );
    }
    return ok();
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { subtitleId: string };
    setSubtitles(
      project,
      currentSubtitles(project).filter(
        (s: Subtitle) => s.id !== params.subtitleId,
      ),
    );
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { subtitleId: string };
    const subtitle = projectBefore.timeline.subtitles.find(
      (s) => s.id === params.subtitleId,
    );
    if (!subtitle) return null;

    return createInverseAction(action, "subtitle/restore", {
      subtitle: { ...subtitle },
    });
  },
};

/**
 * The inverse of a removal. It appends rather than splicing the subtitle back
 * to the index it came from, which is what the executor has always done, so a
 * subtitle undone from the middle of the list lands at the end of it. The one
 * read that order reaches is getSubtitleAtTime, which takes the first cue
 * covering the playhead, so it shows only where two cues overlap. Splicing by
 * index, the way marker/restore does, would be a behaviour change and this
 * migration is not making one.
 */
const subtitleRestore: ActionHandler = {
  type: "subtitle/restore",

  validate(action: Action): ValidationResult {
    const subtitle = (action.params as { subtitle?: Subtitle }).subtitle;
    if (!subtitle || typeof subtitle.id !== "string") {
      return err(
        "subtitle/restore requires a subtitle with an id",
        "params.subtitle",
      );
    }
    return ok();
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { subtitle: Subtitle };
    setSubtitles(project, [...currentSubtitles(project), params.subtitle]);
  },

  invert(action: Action): Action | null {
    const subtitle = (action.params as { subtitle?: Subtitle }).subtitle;
    if (!subtitle) return null;
    return createInverseAction(action, "subtitle/remove", {
      subtitleId: subtitle.id,
    });
  },
};

/**
 * The inverse of an import, and of itself. Distinct from subtitle/setAll in
 * bulk-set.ts, which the caption UI dispatches directly.
 */
const subtitleRestoreAll: ActionHandler = {
  type: "subtitle/restoreAll",

  validate(action: Action): ValidationResult {
    const subtitles = (action.params as { subtitles?: unknown }).subtitles;
    if (!Array.isArray(subtitles)) {
      return err(
        "subtitle/restoreAll requires a subtitles array",
        "params.subtitles",
      );
    }
    return ok();
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { subtitles: Subtitle[] };
    setSubtitles(project, params.subtitles);
  },

  invert(action: Action, projectBefore: Project): Action | null {
    return createInverseAction(action, "subtitle/restoreAll", {
      subtitles: projectBefore.timeline.subtitles.map((s) => ({ ...s })),
    });
  },
};

const subtitleUpdate: ActionHandler = {
  type: "subtitle/update",

  validate(action: Action): ValidationResult {
    const params = action.params as {
      subtitleId?: unknown;
      startTime?: unknown;
      endTime?: unknown;
    };
    const errors: ValidationError[] = [];

    if (!params.subtitleId || typeof params.subtitleId !== "string") {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Subtitle ID is required and must be a string",
        path: "params.subtitleId",
      });
    }

    if (
      params.startTime !== undefined &&
      (typeof params.startTime !== "number" || params.startTime < 0)
    ) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Start time must be a non-negative number",
        path: "params.startTime",
      });
    }

    if (
      params.endTime !== undefined &&
      (typeof params.endTime !== "number" || params.endTime < 0)
    ) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "End time must be a non-negative number",
        path: "params.endTime",
      });
    }

    return collect(errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as {
      subtitleId: string;
      text?: string;
      startTime?: number;
      endTime?: number;
    };
    setSubtitles(
      project,
      currentSubtitles(project).map((s: Subtitle) =>
        s.id === params.subtitleId
          ? {
              ...s,
              ...(params.text !== undefined && { text: params.text }),
              ...(params.startTime !== undefined && {
                startTime: params.startTime,
              }),
              ...(params.endTime !== undefined && {
                endTime: params.endTime,
              }),
            }
          : s,
      ),
    );
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { subtitleId: string };
    const subtitle = projectBefore.timeline.subtitles.find(
      (s) => s.id === params.subtitleId,
    );
    if (!subtitle) return null;

    return createInverseAction(action, "subtitle/update", {
      subtitleId: params.subtitleId,
      text: subtitle.text,
      startTime: subtitle.startTime,
      endTime: subtitle.endTime,
    });
  },
};

const subtitleSetStyle: ActionHandler = {
  type: "subtitle/setStyle",

  validate(action: Action): ValidationResult {
    const style = (action.params as { style?: unknown }).style;
    if (!style || typeof style !== "object") {
      return err("Style is required and must be an object", "params.style");
    }
    return ok();
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { style: SubtitleStyle };
    setSubtitles(
      project,
      currentSubtitles(project).map((s: Subtitle) => ({
        ...s,
        style: params.style,
      })),
    );
  },

  invert(action: Action, projectBefore: Project): Action | null {
    // One style covers the whole list, so the first entry stands in for all of
    // them. A list whose entries carried different styles is flattened by the
    // undo, and a list where none had one undoes to an explicit null that this
    // handler's own validate would reject if it ever came back through
    // execute(). Undo applies inverses without validating, so it does not.
    const firstSubtitle = projectBefore.timeline.subtitles[0];
    return createInverseAction(action, "subtitle/setStyle", {
      style: firstSubtitle?.style ?? null,
    });
  },
};

const subtitleImport: ActionHandler = {
  type: "subtitle/import",

  validate(action: Action): ValidationResult {
    const srtContent = (action.params as { srtContent?: unknown }).srtContent;
    if (!srtContent || typeof srtContent !== "string") {
      return err(
        "SRT content is required and must be a string",
        "params.srtContent",
      );
    }
    return ok();
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { srtContent: string };
    const srtRegex =
      /(\d+)\n(\d{2}:\d{2}:\d{2},\d{3}) --> (\d{2}:\d{2}:\d{2},\d{3})\n([\s\S]*?)(?=\n\n|\n*$)/g;
    let match: RegExpExecArray | null;
    const newSubtitles: Subtitle[] = [];

    while ((match = srtRegex.exec(params.srtContent)) !== null) {
      const startTime = parseSrtTime(match[2]);
      const endTime = parseSrtTime(match[3]);
      const text = match[4].trim();

      newSubtitles.push({
        id: `subtitle-${crypto.randomUUID()}-${match[1]}`,
        text,
        startTime,
        endTime,
      });
    }

    // Not an unconditional assignment: an SRT that parses to nothing leaves the
    // list object identical, which is what the executor did.
    if (newSubtitles.length > 0) {
      setSubtitles(project, [...currentSubtitles(project), ...newSubtitles]);
    }
  },

  invert(action: Action, projectBefore: Project): Action | null {
    return createInverseAction(action, "subtitle/restoreAll", {
      subtitles: projectBefore.timeline.subtitles.map((s) => ({ ...s })),
    });
  },
};

for (const handler of [
  subtitleAdd,
  subtitleRemove,
  subtitleRestore,
  subtitleRestoreAll,
  subtitleUpdate,
  subtitleSetStyle,
  subtitleImport,
]) {
  registerActionHandler(handler);
}
